/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * Text Logo Builder - Multi-step text-to-logo generator
 * Updated: 2026-01-12
 *
 * Features:
 * 1. Text input with multiline support (\n for line breaks)
 * 2. Font selection from FONT_COLLECTIONS (grouped by category)
 * 3. Text alignment (left, center, right)
 * 4. Generate button with loading screen
 * 5. Preview display
 * 6. All client-side Canvas rendering
 */

import React, { useState } from 'react';
import { Sparkles, Palette, Undo, Redo } from 'lucide-react';
import { renderLogoToImage } from '../../lib/utils';
import { indexedDBService } from '../../services/indexedDbService';
import { modalApi } from '../../services/modalApi';
import { FONT_COLLECTIONS, DEFAULT_FONT, PRESET_COLORS, DEFAULT_COLOR } from '../../data/fonts';

interface TextLogoBuilderProps {
  onLogoGenerated: (imageData: {
    imageBase64: string;
    imageId: string;
    imagePath: string;
  }) => void;
  disabled?: boolean;
}

type TextAlign = 'left' | 'center' | 'right';

interface TextLogoState {
  textInput: string;
  fontFamily: string;
  textAlign: TextAlign;
  textColor: string;
}

const TextLogoBuilder: React.FC<TextLogoBuilderProps> = ({ onLogoGenerated, disabled = false }) => {
  // Step 1: Text input
  const [textInput, setTextInput] = useState('');
  const [fontFamily, setFontFamily] = useState(DEFAULT_FONT);
  const [textAlign, setTextAlign] = useState<TextAlign>('center');
  const [textColor, setTextColor] = useState(DEFAULT_COLOR);
  const [showColorModal, setShowColorModal] = useState(false);

  // Step 2: Generation
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('');

  // Step 3: Preview
  const [logoPreview, setLogoPreview] = useState<string>('');

  // Undo/Redo History
  const [history, setHistory] = useState<TextLogoState[]>([]);
  const [redoStack, setRedoStack] = useState<TextLogoState[]>([]);

  // Save current state to history before making changes
  const saveToHistory = () => {
    setHistory((prev) => [...prev, { textInput, fontFamily, textAlign, textColor }]);
    setRedoStack([]); // Clear redo stack on new action
  };

  // Undo handler
  const handleUndo = () => {
    if (history.length === 0) return;
    const newHistory = [...history];
    const lastState = newHistory.pop();
    if (lastState) {
      setRedoStack((prev) => [...prev, { textInput, fontFamily, textAlign, textColor }]);
      setTextInput(lastState.textInput);
      setFontFamily(lastState.fontFamily);
      setTextAlign(lastState.textAlign);
      setTextColor(lastState.textColor);
    }
    setHistory(newHistory);
  };

  // Redo handler
  const handleRedo = () => {
    if (redoStack.length === 0) return;
    const newRedoStack = [...redoStack];
    const nextState = newRedoStack.pop();
    if (nextState) {
      setHistory((prev) => [...prev, { textInput, fontFamily, textAlign, textColor }]);
      setTextInput(nextState.textInput);
      setFontFamily(nextState.fontFamily);
      setTextAlign(nextState.textAlign);
      setTextColor(nextState.textColor);
    }
    setRedoStack(newRedoStack);
  };

  const handleGenerateLogo = async () => {
    if (!textInput.trim()) {
      alert('Vui lòng nhập chữ');
      return;
    }

    setGenerating(true);
    setProgress(0);
    setLoadingMessage('Đang tạo logo...');

    try {
      setProgress(25);
      setLoadingMessage('Đang xử lý chữ...');
      const logoBase64 = renderLogoToImage(textInput.trim(), fontFamily, textAlign, textColor);

      if (!logoBase64) throw new Error('Không thể tạo logo');

      setProgress(50);
      setLoadingMessage('Đang lưu bản xem trước...');
      setLogoPreview(logoBase64);
      setProgress(75);

      try {
        setLoadingMessage('Đang lưu vào bộ nhớ...');
        const imageId = crypto.randomUUID();
        await indexedDBService.saveDesignImage(imageId, logoBase64);
      } catch (cacheErr) {
        console.warn('⚠️ Cache failed');
      }

      setProgress(85);
      setLoadingMessage('Sắp xong rồi, bạn đợi một tí nhé !');

      const uploadResp = await modalApi.uploadImage(logoBase64, undefined, undefined, 'original');

      if (!uploadResp.success) throw new Error(uploadResp.message);

      setProgress(100);
      setLoadingMessage('Hoàn tất!');

      // Small delay to show completion
      setTimeout(() => {
        onLogoGenerated({
          imageBase64: logoBase64,
          imageId: uploadResp.image_id,
          imagePath: uploadResp.filepath,
        });
      }, 500);
    } catch (err: any) {
      console.error('❌ Logo generation failed:', err);
      alert(`Tạo logo thất bại: ${err.message || 'Lỗi không xác định'}`);
      setLogoPreview('');
    } finally {
      setGenerating(false);
      setProgress(0);
      setLoadingMessage('');
    }
  };


  return (
    <div className="w-full h-full relative flex flex-col">
      {/* Floating Toolbar - Undo/Redo (Mobile icon-only, Desktop with labels) */}
      {!logoPreview && (
        <div className="absolute top-2 right-2 bg-white/95 backdrop-blur-md rounded-lg shadow-md border border-gray-100 flex gap-0.5 md:gap-1 p-0.5 md:p-1 z-20">
          {/* Undo Button */}
          <button
            onClick={handleUndo}
            disabled={disabled || history.length === 0}
            className={`group flex items-center justify-center md:justify-start gap-1 md:gap-2 p-0.5 md:p-2 md:px-3 rounded-lg transition-all w-8 h-8 md:w-auto ${
              history.length === 0
                ? 'opacity-40 cursor-not-allowed'
                : 'hover:bg-gray-100 cursor-pointer'
            }`}
            title="Hoàn tác"
          >
            <Undo className={`w-4 h-4 md:w-4 md:h-4 flex-shrink-0 ${history.length === 0 ? 'text-gray-400' : 'text-black'}`} />
            <span className="hidden md:inline text-xs font-bold text-black">Undo</span>
          </button>

          {/* Redo Button */}
          <button
            onClick={handleRedo}
            disabled={disabled || redoStack.length === 0}
            className={`group flex items-center justify-center md:justify-start gap-1 md:gap-2 p-0.5 md:p-2 md:px-3 rounded-lg transition-all w-8 h-8 md:w-auto ${
              redoStack.length === 0
                ? 'opacity-40 cursor-not-allowed'
                : 'hover:bg-gray-100 cursor-pointer'
            }`}
            title="Làm lại"
          >
            <Redo className={`w-4 h-4 md:w-4 md:h-4 flex-shrink-0 ${redoStack.length === 0 ? 'text-gray-400' : 'text-black'}`} />
            <span className="hidden md:inline text-xs font-bold text-black">Redo</span>
          </button>
        </div>
      )}

      {/* Loading Overlay */}
      {generating && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] rounded-lg">
          <div className="bg-white rounded-lg p-8 text-center max-w-sm mx-auto shadow-2xl">
            <div className="w-12 h-12 border-4 border-gray-200 border-t-cyan-500 rounded-full animate-spin mx-auto mb-4" />
            <p className="text-sm font-semibold text-gray-900 mb-2">{loadingMessage}</p>
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <div
                className="bg-cyan-500 h-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">{progress}%</p>
          </div>
        </div>
      )}

      {/* Color Selection Modal */}
      {showColorModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end lg:items-center justify-center z-[9998] p-4">
          <div className="bg-white rounded-t-lg lg:rounded-lg shadow-2xl w-full lg:max-w-lg max-h-[90vh] lg:max-h-[85vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 lg:p-6 flex items-center justify-between">
              <h2 className="text-base lg:text-lg font-bold text-gray-900">Chọn Màu</h2>
              <button
                onClick={() => setShowColorModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Đóng"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-4 lg:p-6 space-y-6">
              {/* Current Color Display */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Màu hiện tại</label>
                <div className="flex items-center gap-3">
                  <div
                    className="w-16 h-16 lg:w-20 lg:h-20 rounded-lg border-2 border-gray-300 shadow-md flex-shrink-0"
                    style={{ backgroundColor: textColor }}
                  />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900">{textColor.toUpperCase()}</p>
                    <p className="text-xs text-gray-500 mt-1">HEX Color Code</p>
                  </div>
                </div>
              </div>

              {/* Custom Color Picker */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Chọn từ bảng màu</label>
                <input
                  type="color"
                  value={textColor}
                  onChange={(e) => setTextColor(e.target.value)}
                  disabled={disabled || generating}
                  className="w-full h-24 lg:h-32 border-2 border-gray-200 rounded-lg cursor-pointer"
                />
              </div>

              {/* Preset Colors */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Màu phổ biến</label>
                <div className="grid grid-cols-6 lg:grid-cols-8 gap-2">
                  {PRESET_COLORS.map((preset) => (
                    <button
                      key={preset.hex}
                      onClick={() => setTextColor(preset.hex)}
                      disabled={disabled || generating}
                      className={`relative group transition-all ${ textColor === preset.hex ? 'ring-2 ring-offset-2 ring-cyan-500 shadow-lg' : ''}`}
                      title={preset.name}
                    >
                      <div
                        className="w-12 h-12 lg:w-14 lg:h-14 rounded-lg border-2 border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                        style={{ backgroundColor: preset.hex }}
                      />
                      <span className="absolute inset-0 flex items-center justify-center text-white font-bold text-lg opacity-0 group-hover:opacity-100 transition-opacity bg-black/20 rounded-lg">
                        ✓
                      </span>
                      <p className="text-[10px] lg:text-xs text-gray-600 mt-1 text-center font-medium">{preset.name}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Confirm Button */}
              <div className="flex gap-2 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowColorModal(false)}
                  className="flex-1 py-2.5 lg:py-3 px-4 border border-gray-200 rounded-lg text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-colors"
                >
                  Hủy
                </button>
                <button
                  onClick={() => setShowColorModal(false)}
                  className="flex-1 py-2.5 lg:py-3 px-4 rounded-lg text-white font-semibold text-sm transition-colors"
                  style={{ backgroundColor: 'rgb(6, 182, 212)' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgb(4, 160, 185)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgb(6, 182, 212)';
                  }}
                >
                  Xong
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Layout: When preview exists, use 2-column on desktop. Otherwise, single column */}
      <div className={`w-full flex-1 overflow-y-auto ${logoPreview ? 'flex flex-col lg:grid lg:grid-cols-2 gap-2 lg:gap-3 lg:items-start' : 'space-y-2 overflow-y-auto'}`}>

        {/* LEFT: Controls Column */}
        <div className={`space-y-2 ${logoPreview ? 'lg:order-1' : ''}`}>
          {/* Input Group */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Nội dung</label>
                <textarea
                  value={textInput}
                  onChange={(e) => {
                    if (textInput !== e.target.value) {
                      saveToHistory();
                      setTextInput(e.target.value);
                    }
                  }}
                  placeholder="VD: AirX COFFEE..."
                  rows={3}
                  disabled={disabled || generating || !!logoPreview}
                  className="w-full p-2.5 border border-gray-200 rounded-lg outline-none resize-none text-base bg-gray-50/50"
                  style={{
                    outline: 'none',
                    boxShadow: 'none',
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(6, 182, 212, 0.1), 0 0 0 2px rgb(6, 182, 212)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
              </div>

              {!logoPreview && (
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Chọn Font</label>
                  <select
                    value={fontFamily}
                    onChange={(e) => {
                      if (fontFamily !== e.target.value) {
                        saveToHistory();
                        setFontFamily(e.target.value);
                      }
                    }}
                    disabled={disabled || generating}
                    className="w-full px-2 py-2 border border-gray-200 rounded-lg outline-none text-sm bg-white"
                    onFocus={(e) => {
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(6, 182, 212, 0.1), 0 0 0 2px rgb(6, 182, 212)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    {FONT_COLLECTIONS.map((category) => (
                      <optgroup key={category.category} label={category.category}>
                        {category.fonts.map((font) => (
                          <option key={font.family} value={font.family}>
                            {font.name}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
              )}
          </div>

          {/* Color Selection Button */}
          {!logoPreview && (
            <button
              onClick={() => setShowColorModal(true)}
              disabled={disabled || generating}
              className="w-full flex items-center justify-between px-3 py-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <span className="flex items-center gap-2">
                <Palette className="w-4 h-4 text-gray-700" />
                <span className="text-sm font-semibold text-gray-700">Chọn Màu</span>
              </span>
              <div className="flex items-center gap-2">
                <div
                  className="w-5 h-5 rounded border border-gray-300 shadow-sm flex-shrink-0"
                  style={{ backgroundColor: textColor }}
                  title={`Color: ${textColor}`}
                />
                <span className="text-xs font-mono text-gray-600">{textColor}</span>
              </div>
            </button>
          )}

          {/* Alignment & Action */}
          {!logoPreview && (
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex-1 min-w-[120px] space-y-1">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Căn lề</label>
                <div className="flex p-0.5 bg-gray-100 rounded-lg gap-0.5">
                    {(['left', 'center', 'right'] as TextAlign[]).map((align) => (
                        <button
                            key={align}
                            onClick={() => {
                              if (textAlign !== align) {
                                saveToHistory();
                                setTextAlign(align);
                              }
                            }}
                            className={`flex-1 py-1 px-1 rounded-md text-[9px] font-bold transition-all ${
                                textAlign === align ? 'bg-white shadow-sm' : 'text-gray-400 hover:text-gray-600'
                            }`}
                            style={textAlign === align ? { color: 'rgb(6, 182, 212)' } : {}}
                        >
                            {align === 'left' ? 'T' : align === 'center' ? 'G' : 'P'}
                        </button>
                    ))}
                </div>
              </div>

              <button
                onClick={handleGenerateLogo}
                disabled={disabled || generating || !textInput.trim()}
                className="flex-[1.5] min-w-[100px] py-2 px-3 text-white font-bold rounded-lg transition-all flex items-center justify-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                style={{
                  backgroundColor: 'rgb(6, 182, 212)',
                  boxShadow: '0 4px 6px -1px rgba(6, 182, 212, 0.3)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgb(4, 160, 185)';
                }}
                onMouseLeave={(e) => {
                  if (!e.currentTarget.disabled) {
                    e.currentTarget.style.backgroundColor = 'rgb(6, 182, 212)';
                  }
                }}
              >
                {generating ? (
                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : <Sparkles className="w-3 h-3" />}
                <span>TẠO</span>
              </button>
            </div>
          )}
        </div>

        {/* RIGHT: Logo Preview Area */}
        {logoPreview && (
          <div className="rounded-lg p-2 lg:p-3 flex flex-col items-center justify-center lg:order-2 min-h-[200px] lg:min-h-[300px] w-full" style={{ backgroundColor: 'rgba(6, 182, 212, 0.08)', border: '1px solid rgba(6, 182, 212, 0.3)' }}>
            <div className="w-32 h-32 lg:w-48 lg:h-48 bg-white rounded-lg p-2 lg:p-3 flex items-center justify-center flex-shrink-0 shadow-md" style={{ border: '1px solid rgba(6, 182, 212, 0.3)' }}>
              <img src={logoPreview} alt="Preview" className="max-w-full max-h-full object-contain" />
            </div>
            <div className="flex-1 min-w-0 mt-3 w-full flex flex-col items-center">
              <p className="text-xs lg:text-sm font-bold uppercase text-center" style={{ color: 'rgb(6, 182, 212)' }}>Logo Preview</p>
              <div className="text-[10px] lg:text-xs mt-2 flex flex-col items-center gap-1 min-w-0">
                <span className="line-clamp-2 text-center min-w-0" style={{ color: 'rgb(6, 182, 212)' }}>
                  {fontFamily}
                </span>
                <span
                  className="inline-block w-3 h-3 lg:w-4 lg:h-4 rounded border-2 border-gray-400 flex-shrink-0"
                  style={{ backgroundColor: textColor }}
                />
                <span className="font-mono text-[9px] lg:text-[10px] flex-shrink-0" style={{ color: 'rgb(6, 182, 212)' }}>{textColor}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TextLogoBuilder;
